import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import WidgetModel, { WidgetTypes } from '@mm/state/model/WidgetModel';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import {
  WidgetContainerRefProp,
  WidgetAlerts,
} from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import { generatePageStructure } from '@mm/state/helpers/filterable-select-helpers';
import PrimarySelect, {
  DropdownOption,
} from '../../components/PrimarySelect/PrimarySelect';
import { useNavigateWidgetHandlers } from '../../helpers/widget-helpers/use-navigate-widget-handlers';
import { ActivePathSelection } from '../common/ActivePathSelection/ActivePathSelection';
import { PathGraphContext } from '../common/PathGraph/PathGraph';
import { WidgetControls } from '../common/WidetControls/WidgetControls';
import './NavigateToDialogueWidget.pcss';
import { AudienceSelector } from '../../../editor/features/campaigns/components/AudienceSelector/AudienceSelector';
import { PlatformType } from '@mm/state/model/CampaignsModel';

type Props = {
  widget: WidgetModel;
  isStartBotAssociateDialogue?: boolean;
  draggableProvided?: DraggableProvided;
  isSmsRelated?: boolean;
  firstWidgetNotAllowedWarning?: WidgetAlerts;
  widgetsToExclude?: WidgetTypes[];
  platforms?: PlatformType[];
  usedInSlideout?: boolean;
} & WidgetContainerRefProp;

const useDefaultSelectedItem = (widget: WidgetModel) => {
  const pages = useSelector((state: StoreState) => state.page.entities);

  if (widget.config.pageId) {
    return pages.find(item => item.id === widget.config.pageId);
  }
};

export const NavigateToDialogueWidget = (props: Props) => {
  const {
    widget,
    isStartBotAssociateDialogue,
    draggableProvided,
    isSmsRelated,
    firstWidgetNotAllowedWarning,
    widgetsToExclude,
    platforms,
    usedInSlideout,
  } = props;

  const widgetCRUDActions = useWidgetCRUDActions();
  const audiencesCRUDActions = useAudiencesCRUDActions();
  const pageCRUDActions = usePageCRUDActions();

  const navigateWidgetHandlers = useNavigateWidgetHandlers();
  const activeChatbot = useSelector((state: StoreState) =>
    activeChatbotSelector(state),
  );
  const pages = useSelector((state: StoreState) => state.page.entities);
  const pageGroups = useSelector(
    (state: StoreState) => state.pageGroup.entities,
  );

  const { setActiveNode, removeNodeAndSuccessors } =
    useContext(PathGraphContext);

  const everyAudienceOption = {
    id: -1,
    name: '<Everyone>',
  };
  // Note: using as to make it usable as a Dropdown option
  const audiencesOptions = [
    everyAudienceOption as AudiencesModel,
    ...useSelector((state: StoreState) => state.audiences.entities),
  ];
  const dialoguesDropdownStructure = generatePageStructure(pages, pageGroups);
  const defaultSelectedItem = useDefaultSelectedItem(widget);

  const selectedAudienceId = widget.config.audienceId;

  // Note: original version of the component had too many intricate interdependencies that
  // made it verby buggy and hard to reason about.
  // The complexity of it really a nice fit for a StateMachine/StateChart.
  // However our current version of th Xstate and useMachine hooks is slightly outdated.
  // Introducing Xstate here without an upgrade would lead to more tech debt.
  // And I do not have enough time to invest in the refactoring needed to upgrade Xstate now.
  // So taking one step closer with React's useReducer, which will already help with the complexity
  // and make it quite easy to move to Xstate later.

  type Action =
    | { type: 'init' }
    | { type: 'toggleShowPath' }
    | { type: 'toggleIsInitialDropdownItemSelection' }
    | { type: 'selectedAudienceOptionChanged' }
    | { type: 'setSelectedDropdownItem'; payload: DropdownOption | undefined };

  const initialState = {
    isInitialDropdownItemSelection: true,
    showPath: false,
    selectedDropdownItem: defaultSelectedItem as DropdownOption | undefined,
  };

  const reducer = (state: typeof initialState, action: Action) => {
    switch (action.type) {
      case 'init':
        const getAudiences = async () => {
          await audiencesCRUDActions.retrieveAll(activeChatbot.id, 0, 100);
          setTimeout(
            () => dispatch({ type: 'toggleIsInitialDropdownItemSelection' }),
            10,
          );
        };
        // Note: 1 audience will always be present, since it is a placeholder one
        if (audiencesOptions.length === 1) {
          getAudiences();
        } else {
          // Note: timeout is needed here as otherwise dispatch be occasionally not defined
          setTimeout(
            () => dispatch({ type: 'toggleIsInitialDropdownItemSelection' }),
            10,
          );
        }
        if (pages.length === 0) {
          pageCRUDActions.retrieveAll(activeChatbot.id);
        }
        if (selectedAudienceId === -1) {
          setActiveNode?.({
            id: widget.id,
            dialogueId: Number(defaultSelectedItem?.id),
            type: 'widget',
            action: 'open',
          });
        }
        return {
          ...state,
        };
      case 'toggleIsInitialDropdownItemSelection': {
        if (state.isInitialDropdownItemSelection && selectedAudienceId !== -1) {
          setActiveNode?.({ id: widget.id, type: 'widget', action: 'close' });
        }
        return {
          ...state,
          isInitialDropdownItemSelection: !state.isInitialDropdownItemSelection,
        };
      }
      case 'toggleShowPath': {
        if (state.showPath) {
          setActiveNode?.({ id: widget.id, type: 'widget', action: 'close' });
        } else if (state.selectedDropdownItem && selectedAudienceId !== -1) {
          setActiveNode?.({
            id: widget.id,
            dialogueId: Number(state.selectedDropdownItem.id),
            type: 'widget',
            action: 'open',
          });
        }
        return {
          ...state,
          showPath: !state.showPath,
        };
      }
      case 'setSelectedDropdownItem': {
        setActiveNode?.({ id: widget.id, type: 'widget', action: 'close' });
        removeNodeAndSuccessors?.({
          id: widget.id,
          type: 'widget',
          actionType: 'deleteSuccessors',
        });
        if (action.payload) {
          const dialogueId = Number(action?.payload?.id);
          if (dialogueId !== widget.config.pageId) {
            widgetCRUDActions.update({
              ...widget,
              config: {
                ...widget.config,
                pageId: dialogueId,
              },
            });
          }
          if (selectedAudienceId === -1 || state.showPath) {
            setActiveNode?.({
              id: widget.id,
              dialogueId,
              type: 'widget',
              action: 'open',
            });
          }
        }
        return {
          ...state,
          selectedDropdownItem: action.payload,
        };
      }
      case 'selectedAudienceOptionChanged': {
        setActiveNode?.({ id: widget.id, type: 'widget', action: 'close' });

        if (
          !state.isInitialDropdownItemSelection &&
          state.selectedDropdownItem &&
          selectedAudienceId === -1
        ) {
          setActiveNode?.({
            id: widget.id,
            dialogueId: Number(state?.selectedDropdownItem?.id),
            type: 'widget',
            action: 'open',
          });
        }
        return { ...state };
      }
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    dispatch({ type: 'init' });
    if (!state.selectedDropdownItem) {
      if (widget.config.pageId) {
        widgetCRUDActions.update({
          ...widget,
          config: {
            ...widget.config,
            pageId: null,
          },
        });
      }
    }
  }, []);

  useEffect(() => {
    // Note: fighting some race conditions between multiple instances of the widgets affecting PathGraph
    setTimeout(() => dispatch({ type: 'selectedAudienceOptionChanged' }), 50);
  }, [selectedAudienceId]);

  const onSelectAudience = (audienceId: number) => {
    navigateWidgetHandlers.updateAudienceId(
      widget,
      audienceId === -1 ? null : audienceId,
    );
  };

  const onCancel = () => {
    navigateWidgetHandlers.updateAudienceId(widget, null);
  };

  const showDialogueText =
    selectedAudienceId === -1
      ? ''
      : state.showPath
      ? 'Hide Dialogue'
      : 'Show Dialogue';

  const beforeDestroy = useCallback(() => {
    setActiveNode?.({ id: widget.id, type: 'widget', action: 'close' });
    removeNodeAndSuccessors?.({
      id: widget.id,
      type: 'widget',
      actionType: 'delete',
      parentId: widget.pageId,
      parentType: 'dialogue',
    });
  }, [widget.id]);

  return (
    <div className="navigate-to-dialogue-widget-wrapper">
      <div className="navigate-to-dialogue-widget">
        <div className="navigate-to-dialogue-widget__main-wrapper">
          <div className="navigate-to-dialogue-widget__line-wrapper">
            <div className="navigate-to-dialogue-widget__left-text">
              Navigate to Dialogue:
            </div>

            <PrimarySelect
              className="navigate-to-dialogue-widget__dialogues-select"
              organizedOptions={dialoguesDropdownStructure}
              onChange={item =>
                dispatch({ type: 'setSelectedDropdownItem', payload: item })
              }
              value={state.selectedDropdownItem}
              showSearchFilter={true}
            />
          </div>

          {!isStartBotAssociateDialogue && (
            <div className="navigate-to-dialogue-widget__line-wrapper">
              <div className="navigate-to-dialogue-widget__left-text">
                For Audience:
              </div>

              <div className="navigate-to-dialogue-widget__dialogues-select">
                <AudienceSelector
                  fallbackId={-1}
                  parentAudiences={[everyAudienceOption as AudiencesModel]}
                  selectedAudienceId={selectedAudienceId}
                  onAudienceSelected={onSelectAudience}
                  onCancel={onCancel}
                  formatText={name => name || 'Everyone'}
                />
              </div>
            </div>
          )}
        </div>

        <div className="navigate-to-dialogue-widget__right">
          <div
            onClick={() => dispatch({ type: 'toggleShowPath' })}
            className="navigate-to-dialogue-widget__go-to-dialogue-button"
          >
            {showDialogueText}
          </div>
        </div>

        <WidgetControls
          widget={widget}
          draggableProvided={draggableProvided}
          beforeDestroy={beforeDestroy}
          platforms={platforms}
        />
      </div>
      <div>
        <ActivePathSelection
          widgetsToExclude={widgetsToExclude}
          chatbotId={activeChatbot.id}
          widget={widget}
          isActive={state.showPath || selectedAudienceId === -1}
          hideClose
          isSmsRelated={isSmsRelated}
          firstWidgetNotAllowedWarning={firstWidgetNotAllowedWarning}
          platforms={platforms}
          usedInSlideout={usedInSlideout}
        />
      </div>
    </div>
  );
};
